import React, { useState } from 'react';
import { 
  Check, 
  Star, 
  Rocket, 
  ShieldCheck, 
  Zap, 
  Calculator, 
  Newspaper, 
  BarChart3, 
  Globe, 
  FileText 
} from 'lucide-react';
import CheckoutModal from '../components/CheckoutModal'; 
import { PLAN_CONFIG } from '../config/planConfig';

const SubscriptionPlan = () => {
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const plan = PLAN_CONFIG.profitable;

  return (
    <div style={styles.container}>
      <style>{`
        .plan-card { transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); position: relative; overflow: hidden; }
        .plan-card:hover { transform: translateY(-12px); border-color: #38bdf8 !important; box-shadow: 0 20px 40px rgba(56, 189, 248, 0.15); }
        .glow { position: absolute; top: -50%; left: -50%; width: 200%; height: 200%; background: radial-gradient(circle, rgba(56,189,248,0.07) 0%, transparent 70%); pointer-events: none; }
        .founding-badge { animation: pulse 2s infinite; }
        @keyframes pulse { 0% { transform: scale(1); } 50% { transform: scale(1.03); } 100% { transform: scale(1); } }
      `}</style>

      <div style={styles.header}>
        <div className="founding-badge" style={styles.badge}>
          <Star size={14} fill="#fbbf24" color="#fbbf24" /> 1st Month FREE with code: FTJ100
        </div>
        <h1 style={styles.mainTitle}>The All-In-One Trading Terminal</h1>
        <p style={styles.subtitle}>
          Stop hopping between multiple websites. Get institutional-grade tools, real-time news, and advanced analytics in one place.
        </p>
      </div>

      <div style={styles.cardContainer}>
        {/* PREMIUM PLAN CARD */}
        <div className="plan-card" style={styles.card}>
          <div className="glow" />
          <div style={styles.popularLabel}>ELITE ACCESS</div>
          
          <div style={styles.planHeader}>
            <div style={styles.iconCircle}>
                <Rocket size={24} color="#38bdf8" />
            </div>
            <h2 style={styles.planName}>{plan.name}</h2>
            <div style={styles.priceContainer}>
              <span style={styles.priceSymbol}>₹</span>
              <span style={styles.priceAmount}>{plan.basePrice.toLocaleString()}</span>
              <span style={styles.period}>/month</span>
            </div>
            <p style={styles.usdEquivalent}>First month is on us. Get 100% off at checkout</p>
          </div>

          <div style={styles.divider} />

          {/* GRID FEATURE LIST */}
          <div style={styles.featureGrid}>
            <div style={styles.featureItem}>
              <div style={styles.checkWrapper}><Calculator size={16} color="#38bdf8" /></div>
              <span><b>Futures & Forex</b> Lot Calculator</span>
            </div>
            <div style={styles.featureItem}>
              <div style={styles.checkWrapper}><Newspaper size={16} color="#38bdf8" /></div>
              <span><b>Real-time</b> News & Event Center</span>
            </div>
            <div style={styles.featureItem}>
              <div style={styles.checkWrapper}><BarChart3 size={16} color="#38bdf8" /></div>
              <span>Advanced <b>Equity Curve</b> Analytics</span>
            </div>
            <div style={styles.featureItem}>
              <div style={styles.checkWrapper}><Globe size={16} color="#38bdf8" /></div>
              <span>Global <b>Market Session</b> Tracking</span>
            </div>
            <div style={styles.featureItem}>
              <div style={styles.checkWrapper}><FileText size={16} color="#38bdf8" /></div>
              <span>Institutional <b>PDF Exports</b></span>
            </div>
            <div style={styles.featureItem}>
              <div style={styles.checkWrapper}><Check size={16} color="#38bdf8" strokeWidth={3} /></div>
              <span><b>Unlimited</b> Trade Logging</span>
            </div>
          </div>

          <button 
            style={styles.ctaButton} 
            onClick={() => setIsCheckoutOpen(true)}
          >
            Claim Founding Access
          </button>
          
          <p style={styles.promoNote}>Apply <b>FTJ100</b> at checkout for <b>100% OFF</b> for your first month </p>
        </div>
      </div>

      {/* TRUST FOOTER */}
      <div style={styles.trustFooter}>
        <div style={styles.trustItem}>
            <ShieldCheck size={20} color="#10b981" />
            <span>Secure Encryption</span>
        </div>
        <div style={styles.trustItem}>
            <Zap size={20} color="#fbbf24" />
            <span>Instant Pro Activation</span>
        </div>
      </div>

      <CheckoutModal 
        isOpen={isCheckoutOpen} 
        onClose={() => setIsCheckoutOpen(false)} 
      />
    </div>
  );
};

const styles = {
  container: { padding: '60px 20px', background: 'transparent', minHeight: '100vh', color: '#fff' },
  header: { textAlign: 'center', marginBottom: '50px' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)', color: '#fbbf24', padding: '10px 20px', borderRadius: '100px', fontSize: '12px', fontWeight: '800', marginBottom: '24px', letterSpacing: '1px' },
  mainTitle: { fontSize: '42px', fontWeight: '900', marginBottom: '16px', letterSpacing: '-1px', background: 'linear-gradient(to right, #fff, #94a3b8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  subtitle: { fontSize: '18px', color: '#64748b', maxWidth: '650px', margin: '0 auto', lineHeight: '1.6' },
  
  cardContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
  card: { width: '100%', maxWidth: '500px', background: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(20px)', borderRadius: '32px', padding: '40px', border: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', flexDirection: 'column', position: 'relative' },
  
  popularLabel: { position: 'absolute', top: '24px', right: '-35px', background: '#38bdf8', color: '#000', padding: '6px 45px', transform: 'rotate(45deg)', fontSize: '10px', fontWeight: '900', letterSpacing: '1px' },
  
  iconCircle: { width: '56px', height: '56px', background: 'rgba(56, 189, 248, 0.1)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', alignSelf: 'center' },
  planName: { fontSize: '22px', fontWeight: '800', marginBottom: '12px', color: '#f8fafc', textAlign: 'center' },
  priceContainer: { display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '4px' },
  priceSymbol: { fontSize: '24px', fontWeight: '600', color: '#38bdf8' },
  priceAmount: { fontSize: '60px', fontWeight: '900', letterSpacing: '-2px' },
  period: { fontSize: '18px', color: '#64748b' },
  usdEquivalent: { fontSize: '13px', color: '#475569', marginTop: '4px', textAlign: 'center' },
  
  divider: { width: '100%', height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.1), transparent)', margin: '30px 0' },
  
  featureGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '40px' },
  featureItem: { display: 'flex', alignItems: 'center', gap: '14px', fontSize: '15px', color: '#94a3b8' },
  checkWrapper: { width: '32px', height: '32px', background: 'rgba(56, 189, 248, 0.08)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  
  ctaButton: { width: '100%', padding: '20px', borderRadius: '18px', border: 'none', background: '#38bdf8', color: '#000', fontWeight: '900', fontSize: '16px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 10px 20px rgba(56, 189, 248, 0.2)' },
  promoNote: { marginTop: '20px', fontSize: '12px', color: '#475569', textAlign: 'center' },

  trustFooter: { marginTop: '40px', display: 'flex', gap: '32px', justifyContent: 'center' },
  trustItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', fontWeight: '700' }
};

export default SubscriptionPlan;